import type { PortalLocale } from '../lib/locale';

/**
 * The words that belong to the portal itself rather than to any one page.
 *
 * Business-Day Close is captured with the whole shell visible. Keeping these words beside one
 * another prevents a Spanish decision screen from being framed by an English header and sidebar,
 * and gives every route exactly the same locale that the page below it derives from the browser.
 */
export interface AppLayoutCopy {
  operations: string;
  openNavigation: string;
  account: (name: string) => string;
  devBypass: string;
  globalAdministrator: string;
  signOutUnavailable: string;
  signOut: string;
  navigation: {
    dashboard: string;
    menuGroup: string;
    menuItems: string;
    pricing: string;
    tax: string;
    peopleGroup: string;
    staff: string;
    payroll: string;
    customers: string;
    serviceGroup: string;
    floor: string;
    devices: string;
    register: string;
    fiscal: string;
    channels: string;
    referrals: string;
    stockGroup: string;
    inventory: string;
    suppliers: string;
    recordsGroup: string;
    reports: string;
    filedMonths: string;
    audit: string;
    settings: string;
    mcp: string;
  };
}

const EN: AppLayoutCopy = {
  operations: 'Operations',
  openNavigation: 'Open navigation',
  account: (name) => `Account: ${name}`,
  devBypass: 'dev bypass — no Entra session',
  globalAdministrator: 'Global Administrator',
  signOutUnavailable: 'Sign-out unavailable in dev bypass',
  signOut: 'Sign out',
  navigation: {
    dashboard: 'Dashboard',
    menuGroup: 'Menu',
    menuItems: 'Menu items',
    pricing: 'Prices & happy hour',
    tax: 'Tax',
    peopleGroup: 'People',
    staff: 'Staff',
    payroll: 'Time & pay',
    customers: 'Customers',
    serviceGroup: 'Service',
    floor: 'Floor plan',
    devices: 'Devices',
    register: 'Register & cash',
    fiscal: 'Invoicing (CAI)',
    channels: 'Delivery platforms',
    referrals: 'Referral partners',
    stockGroup: 'Stock & suppliers',
    inventory: 'Stock & recipes',
    suppliers: 'Suppliers & bills',
    recordsGroup: 'Records',
    reports: 'Reports',
    filedMonths: 'Filed months',
    audit: 'Activity log',
    settings: 'Settings',
    mcp: 'Assistant access',
  },
};

const ES: AppLayoutCopy = {
  operations: 'Operaciones',
  openNavigation: 'Abrir navegación',
  account: (name) => `Cuenta: ${name}`,
  devBypass: 'modo de desarrollo — sin sesión de Entra',
  globalAdministrator: 'Administrador global',
  signOutUnavailable: 'No se puede cerrar sesión en modo de desarrollo',
  signOut: 'Cerrar sesión',
  navigation: {
    dashboard: 'Panel principal',
    menuGroup: 'Menú',
    menuItems: 'Productos del menú',
    pricing: 'Precios y hora feliz',
    tax: 'Impuestos',
    peopleGroup: 'Personas',
    staff: 'Personal',
    payroll: 'Horarios y pagos',
    customers: 'Clientes',
    serviceGroup: 'Servicio',
    floor: 'Plano del bar',
    devices: 'Dispositivos',
    register: 'Caja y efectivo',
    fiscal: 'Facturación (CAI)',
    channels: 'Plataformas de entrega',
    referrals: 'Socios de referencia',
    stockGroup: 'Inventario y proveedores',
    inventory: 'Inventario y recetas',
    suppliers: 'Proveedores y facturas',
    recordsGroup: 'Registros',
    reports: 'Informes',
    filedMonths: 'Meses declarados',
    audit: 'Registro de actividad',
    settings: 'Configuración',
    mcp: 'Acceso del asistente',
  },
};

export function appLayoutCopy(locale: PortalLocale): AppLayoutCopy {
  return locale === 'es-HN' ? ES : EN;
}
